import { Injectable, inject, signal } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TokenService } from '../../auth/services/token.service';
import { AlertHistoryResponse } from '../models/notification.model';

/**
 * STOMP-over-WebSocket realtime client for incoming alerts.
 *
 * Connects to the gateway: `${apiURL → ws}/notification/ws`.
 * Authentication is performed at the STOMP CONNECT frame with the same JWT
 * used for REST calls (handled by {@link WebSocketAuthInterceptor} server-side).
 *
 * Subscriptions:
 *  - `/user/queue/alerts`  — per-user targeted notifications
 *  - `/topic/alerts`        — broadcast notifications (sendToAll = true)
 */
@Injectable({ providedIn: 'root' })
export class NotificationRealtimeService {
  private readonly tokenService = inject(TokenService);

  private client: Client | null = null;
  private subscriptions: StompSubscription[] = [];

  private readonly alert$ = new Subject<AlertHistoryResponse>();
  readonly connected = signal(false);

  /** Stream of every notification received (personal + broadcast). */
  get alerts$(): Observable<AlertHistoryResponse> {
    return this.alert$.asObservable();
  }

  /** Establish the STOMP connection. Safe to call multiple times. */
  connect(): void {
    if (this.client?.active) {
      return;
    }
    const token = this.tokenService.getAccessToken();
    if (!token) {
      console.warn('[NotificationRealtime] No token, skipping connect');
      return;
    }

    this.client = new Client({
      brokerURL: this.buildBrokerUrl(),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        this.connected.set(true);
        this.subscribeAll();
      },
      onStompError: (frame) => {
        console.error('[NotificationRealtime] STOMP error', frame.headers['message']);
      },
      onWebSocketClose: () => {
        this.connected.set(false);
        this.subscriptions = [];
      },
    });

    this.client.activate();
  }

  /** Tear down the STOMP connection (e.g. on logout). */
  disconnect(): void {
    this.subscriptions.forEach((s) => {
      try { s.unsubscribe(); } catch { /* noop */ }
    });
    this.subscriptions = [];
    if (this.client) {
      this.client.deactivate().catch(() => void 0);
      this.client = null;
    }
    this.connected.set(false);
  }

  private subscribeAll(): void {
    if (!this.client) return;

    const handler = (message: IMessage) => {
      try {
        const payload = JSON.parse(message.body) as AlertHistoryResponse;
        this.alert$.next(payload);
      } catch (e) {
        console.error('[NotificationRealtime] Failed to parse payload', e);
      }
    };

    this.subscriptions.push(
      this.client.subscribe('/user/queue/alerts', handler),
      this.client.subscribe('/topic/alerts', handler)
    );
  }

  /** Build ws(s):// URL from `apiURL` and append the gateway-routed `/notification/ws`. */
  private buildBrokerUrl(): string {
    const httpUrl = environment.apiURL.replace(/\/+$/, '');
    const wsUrl = httpUrl.replace(/^http/i, 'ws');
    return `${wsUrl}/notification/ws`;
  }
}
